import { Link } from "react-router-dom";
import { Hexagon } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useConsent } from "@/lib/consent";
import { NewsletterOptIn } from "@/components/newsletter/NewsletterOptIn";

export function Footer() {
  const { openManager } = useConsent();
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const { user } = useAuth();
  const { isAdmin, isInstructor } = useUserRole();

  const footerLinks = useMemo(() => {
    const links = [
      { nameKey: "navigation.home", path: "/" },
      { nameKey: "navigation.courses", path: "/courses" },
      { nameKey: "navigation.pricing", path: "/pricing" },
      { nameKey: "navigation.about", path: "/about" },
      { nameKey: "navigation.community", path: "/community" },
      { nameKey: "navigation.blog", path: "/blog" },
    ];

    // Add authenticated-only links
    if (user) {
      links.push({ nameKey: "navigation.dashboard", path: "/dashboard" });
      links.push({ nameKey: "navigation.gamification", path: "/gamification" });
    }

    // Add role-based links
    if (isAdmin) {
      links.push({ nameKey: "navigation.admin", path: "/admin" });
    }

    if (isInstructor) {
      links.push({ nameKey: "navigation.instructor", path: "/admin/courses" });
    }

    return links;
  }, [user, isAdmin, isInstructor]);

  const legalLinks = [
    { nameKey: "footer.privacyPolicy", path: "/privacy-policy" },
    { nameKey: "footer.termsOfService", path: "/terms-of-service" },
    { nameKey: "footer.refundPolicy", path: "/refund-policy" },
    { nameKey: "footer.cookiePolicy", path: "/cookie-policy" },
  ];

  return (
    <footer className="bg-card/40 backdrop-blur-glass border-t glass-border mt-auto">
      <div className="container mx-auto px-4 lg:px-8 py-4">
        {/* Row 1: Brand + Newsletter */}
        <div className="grid gap-4 md:grid-cols-[1fr_minmax(280px,420px)] items-start mb-3">
          <div className="flex items-center justify-between md:justify-start gap-4">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <Hexagon className="w-6 h-6 text-primary" />
              <span className="text-lg font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                Aicavite
              </span>
            </Link>
            <p className="text-muted-foreground text-xs hidden md:block">
              {t('footer.tagline')}
            </p>
          </div>
          <NewsletterOptIn variant="compact" source="footer" />
        </div>

        {/* Row 2: All Links */}
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 py-3 border-y glass-border">
          {footerLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="text-muted-foreground hover:text-primary transition-colors text-xs"
            >
              {t(link.nameKey)}
            </Link>
          ))}
          <span className="text-muted-foreground/30">•</span>
          {legalLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className="text-muted-foreground hover:text-primary transition-colors text-xs"
            >
              {t(link.nameKey)}
            </Link>
          ))}
          <button
            type="button"
            onClick={openManager}
            className="text-muted-foreground hover:text-primary transition-colors text-xs underline-offset-2 hover:underline"
          >
            {t("consent.footer.preferences", "Preferências de cookies")}
          </button>
        </div>

        {/* Row 3: Copyright */}
        <div className="text-center text-muted-foreground text-xs pt-3">
          <p>&copy; {currentYear} Aicavite. {t('footer.allRightsReserved')}</p>
        </div>
      </div>
    </footer>
  );
}
